import {
    CMV_DEV,
    CMV_PREPROD,
    CMV_PROD, EMPTY_MATERIAL_NUMBER, MTV_DEV,
    MTV_PREPROD, MTV_PROD,
    OTV_DEV,
    OTV_PREPROD,
    OTV_PROD,
    REQUEST_COCKPIT_LINK,
} from './constants';
import {Config} from 'olympe';
import {OneTimeVendor} from '../models/OneTimeVendor';

/**
 * Check whether the price data are given by the API, or not.
 * @param {string} unit
 * @param {number} price
 * @param {string} currency
 * @param {number} per
 * @return {boolean} true if all the data are filled, false otherwise
 */
export const checkPriceDataFromAPI = (unit, price, currency, per) => {
    return !!unit && !!price && !!currency && !!per;
};

/**
 * Check whether the price data are given by the API, or not.
 * @param {number} price
 * @return {boolean} true if all the data are filled, false otherwise
 */
export const checkIfPriceDataFromAPI = (price) => {
    return !!price;
};
// Removes leading zeros from a string.
export const removeLeadingZeros = (str) => {
    return String(str).replace(/^0+/, '');
};

/**
 * Move an element of an array to the first position, and move all the others accordingly
 * @param {any[]} arr
 * @param {any} element
 * @param {function(any, any): boolean=} equalityCheck
 * @param {function(any[], any): boolean=} includesCheck
 * @return {any[]}
 */
export const moveToFirst = (arr, element, equalityCheck, includesCheck) => {
    if (includesCheck ? !includesCheck(arr, element) : !arr.includes(element)) {
        return arr;
    }

    const idx = arr.findIndex((e) => equalityCheck ? equalityCheck(e, element) : e === element);

    return [
        element,
        ...arr.slice(0, idx),
        ...arr.slice(idx+1),
    ];
};

export const stringWithOnlyFirstLetterInCapital = (s) => {
    if (s) {
        return `${s[0]?.toUpperCase()}${s.substring(1).toLowerCase()}`;
    } else {
        return '';
    }
};

/**
     * Returns the OTV and CMV codes based on the environment.
     * @param {string} env
     * @return {Array}
     */
export const getVendorCodes = (env) => {
    const codes = {
        codev: [OTV_DEV, CMV_DEV, MTV_DEV],
        preprod: [OTV_PREPROD, CMV_PREPROD, MTV_PREPROD],
        prod: [OTV_PROD, CMV_PROD, MTV_PROD],
    };
    return codes[env] || [OTV_DEV, CMV_DEV, MTV_DEV];
};

/**
 * Check if the given supplier is either an OTV or a CMV
 * @param {string} supplier The supplier code
 * @return {boolean} True if the supplier is an OTV or a CMV, false otherwise
 */
export const isOTVorCMV = (supplier) => {
    const supplierOn10Digits = supplier.padStart(10, '0');
    switch (Config.getParameter('ENV')) {
    case 'preprod':
        return supplierOn10Digits === OTV_PREPROD || supplierOn10Digits === CMV_PREPROD || supplierOn10Digits === MTV_PREPROD;
    case 'prod':
        return supplierOn10Digits === OTV_PROD || supplierOn10Digits === CMV_PROD || supplierOn10Digits === MTV_PROD;
    case 'codev':
    default:
        return supplierOn10Digits === OTV_DEV || supplierOn10Digits === CMV_DEV || supplierOn10Digits === MTV_DEV;
    }
};

/**
 * Returns the corresponding vendor type (otv, cmv or generic)
 * @param {string} supplier
 * @return {OneTimeVendor.supplierTypes}
 */
export const oTVorCMVorGeneric = (supplier) => {
    const [otv, cmv, mtv] = getVendorCodes(Config.getParameter('ENV'));

    const supplierOn10Digits = supplier.padStart(10, '0');

    return supplierOn10Digits === otv ? OneTimeVendor.supplierTypes.otv :
        supplierOn10Digits === cmv ? OneTimeVendor.supplierTypes.cmv :
            supplierOn10Digits === mtv ? OneTimeVendor.supplierTypes.mtv :
                OneTimeVendor.supplierTypes.generic;
};

/**
 * Returns the request cockpit link corresponding to the environment
 * @param {string} env Must be codev, preprod or prod
 * @return {*}
 */
export const getRequestCockpitLink = (env) => {
    return REQUEST_COCKPIT_LINK[env];
};

/**
 * Funciton to replace specific part of the text with a React component
 * @param {string[]} text
 * @param {string} find
 * @param {*} replace
 * @return {*}
 */
export const replaceJSX = (text, find, replace) => {
    const changedText = text.map((item) => {
        if (Array.isArray(item)) {
            return item[0].split(find)?.flatMap((item) => [item, replace]).slice(0, -1);
        }
        return item.split(find)?.flatMap((item) => [item, replace]).slice(0, -1);
    });
    return changedText;
};

/**
 * Send a custom event to open the vendor dialog
 * @param {*} e
 * @param {string} prTag
 */
export const showVendorDialog = (e, prTag) => {
    e.preventDefault();
    const customEvent = new CustomEvent('openVendorDialog', {
        detail: {prTag: prTag},
    });
    window.dispatchEvent(customEvent);
};

/**
 * Replaces all accented characters with their non-accented counterparts and replaces comma with an empty space
 * @param {string} str
 * @return {string}
 */
export const removeAccents = (str) => {
    return str?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/,/g, ' ') || '';
};

/**
 * Remove leading zeros from a vendor code
 * @param {string} code
 * @return {string}
 */
export const normalizeCode = (code) => {
    if (!code) return '';
    return String(code).replace(/^0+/, '');
};

/**
 * Safely parse a JSON string or return the object if it's already parsed.
 * @param {string|Object} data
 * @return {Object}
 */
export const safeParseJSON = (data) => {
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (error) {
            console.error('Failed to parse JSON:', error);
            return {};
        }
    } else if (typeof data === 'object' && data !== null) {
        return data;
    } else {
        console.warn('Unexpected data type:', typeof data);
        return {};
    }
};

/**
 * Escapes a value for safe inclusion in a SQL query string.
 * This should be used whenever values are inlined into SQL instead of using bindings.
 * @param {*} value
 * @return {string}
 */
export const escapeSQLValue = (value) => {
    if (value === null || value === undefined) {
        return 'NULL';
    }
    if (typeof value === 'number') {
        return value.toString();
    }
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }
    const escaped = String(value).replace(/'/g, `''`);
    return `'${escaped}'`;
};

/**
 * Characters that are special in SQL Server CONTAINS() even after wrapping the
 * term as a phrase. Replaced with spaces (FTS already treats them as word breakers).
 *
 * - `"` and unicode quotes: phrase delimiters. A user typing `foo" OR "bar`
 *   would otherwise become `"foo" OR "bar"` (boolean injection / syntax error).
 * - `*`: prefix wildcard when it is the last character of a quoted phrase (`"nestle*"`).
 * - `~ ( ) [ ] { } & | ! ^ , ;`: CONTAINS operators, grouping, and list separators
 *   that throw "Syntax error near ..." if they ever sit outside a well-formed phrase.
 */
const FTS_SPECIAL_CHARS = /[\0"\u201C\u201D\u201E\u201F*~()[\]{}&|!^,;]/g;

/**
 * Escapes a free-text search value for SQL Server CONTAINS().
 *
 * Wrapping the term in double quotes makes it a phrase, which disables AND/OR/NOT/NEAR.
 * That is not enough on its own: quotes and `*` still have meaning inside a phrase,
 * and an empty phrase (`""`) throws "Null or empty full-text predicate".
 *
 * Single quotes are escaped via {@link escapeSQLValue} so the value cannot break
 * out of the SQL string literal.
 *
 * @param {*} value User-provided search text
 * @return {string|null} SQL string literal for the CONTAINS search condition
 *   (e.g. `'"hello world"'`), or `null` when there is nothing searchable left
 */
export const escapeSQLValueForFullTextIndex = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const sanitized = String(value)
        .replace(FTS_SPECIAL_CHARS, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (!sanitized) {
        return null;
    }

    // Phrase wrap so boolean operators in the user text are treated as words.
    return escapeSQLValue(`"${sanitized}"`);
};

/**
 * Return the material name without the leading zero, and without number if material number is empty.
 * Example: `000000000005732584 - coffee` becomes `5732584 - coffee`, and `EMPTY00 - coffee` becomes `coffee`.
 * @return {string}
 */
export const formatMaterialName = (materialName) => {
    const withoutLeadingZero = removeLeadingZeros(materialName);
    if (withoutLeadingZero.split(' - ')[0] !== EMPTY_MATERIAL_NUMBER) {
        return withoutLeadingZero;
    } else {
        return withoutLeadingZero.split(' - ')[1];
    }
};
